import React, { forwardRef, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { X, Menu, Globe } from 'lucide-react';
import { logo, menuImg1, menuImg2, menuImg3, MLogo } from '../../../assets';
import styles from './header.module.css';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../../i18n';
import { motion } from "framer-motion";
import ToggleSwitch from './ToggleSwitch';
import { LANG_AR, LANG_EN } from '../../helpers/constant';

const Header = forwardRef(({ isHomePage }, ref) => {
    const [isOpen, setIsOpen] = useState(false); // Mobile menu
    const [scrolled, setScrolled] = useState(false);
    const [desktopDrawerOpen, setDesktopDrawerOpen] = useState(false); // Desktop morphing menu
    const logoRef = useRef(null);
    const morphingNavRef = useRef(null); // Morphing navigation container
    const location = useLocation();
    const menuItemRefs = useRef([]);
    const { t, i18n } = useTranslation();

    const [isArabic, setIsArabic] = useState(i18n.language === "ar");

    const toggleLanguage = () => {
        const newLanguage = isArabic ? "en" : "ar";
        setIsArabic(!isArabic);
        i18n.changeLanguage(newLanguage);

    };

    const menuItems = [
        { label: t('our_work'), path: '/our-work' },
        { label: t('What we do'), path: '/what-we-do' },
        { label: t('About us'), path: '/about-us' },
        { label: t('Contact'), path: '/contact-us' },

    ];

    const additionalMenuItems = [
      { label: t('Influencers'), path: '/influencers' },
        { label: t('Podcast'), path: '/podcast' },
       // { label: t('Blogs'), path: '/blogs' },
        { label: t('Live Feeds'), path: '/live-feeds' },
    ];

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (desktopDrawerOpen) {
            document.body.style.overflow = 'hidden';
            // Animate the morphing menu open from the top-right corner
            gsap.to(morphingNavRef.current, {
                duration: 0.9,
                ease: 'power3.out',
            });
            gsap.fromTo(
                menuItemRefs.current,
                { opacity: 0, y: 50 }, // Initial state: invisible and below
                { opacity: 1, y: 0, stagger: 0.2, duration: 1, ease: 'power3.out' } // Animate to visible and original position
            );
        } else {
            // Animate the morphing menu close
            gsap.to(morphingNavRef.current, {
                duration: 0.9,
                ease: 'power3.in',
            });
            document.body.style.overflow = 'visible';
        }
    }, [desktopDrawerOpen]);


    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const toggleDesktopDrawer = () => {
        setDesktopDrawerOpen(prevState => !prevState);
    };

    const backgroundClass = isHomePage && !scrolled ? styles.transparent : styles.solidBlack;


    return (
        <>
            <div className={`${styles.headerP} ${scrolled ? styles.scrolled : ''} ${backgroundClass}`}>
                <div className={styles.header_inner}>
                    <header className={`${styles.header} ${i18n.language === LANG_AR ? 'arab' : ''}`}>
                        <div className={styles.logo}>
                            <Link to="/" className={styles.logoLink} ref={logoRef}>
                                <img src={logo} alt="Logo" className={styles.logoImg} />
                            </Link>
                        </div>
                        <div className={styles.headerRight}>
                            {/* Desktop Menu */}
                            <nav className={styles.menus}>
                                <ul className={`${styles.menusLs} ${i18n.language === LANG_AR ? 'arab' : ''}`}>
                                    {menuItems.map((item, index) => (
                                        <li key={index}>
                                            <div className={`${styles.menu} ${location.pathname === item.path ? styles.active : ''}`}>
                                                <Link to={item.path} ref={ref}>
                                                    {item.label}
                                                </Link>
                                            </div>
                                        </li>
                                    ))}

                                    <li>
                                        <button
                                            className={styles.desktopHamburger}
                                            onClick={toggleDesktopDrawer}
                                            aria-label="Open Desktop Drawer"
                                        >
                                            <Menu />
                                        </button>
                                    </li>
                                    {/* <li className={styles.toggleLang}>
                                        <ToggleSwitch />
                                    </li> */}
                                </ul>
                            </nav>
                            <div className={styles.menuLang}>
                                {/* Mobile Hamburger */}
                                <button className={styles.menuToggle} onClick={toggleMenu}>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </button>
                               {/*  <div>
                                    <ToggleSwitch />
                                </div> */}
                            </div>
                        </div>
                    </header>
                </div>
            </div>
            {/* Mobile Menu */}
            <div className={`${styles.mobileMenu} ${isOpen ? styles.open : ''}`}>
                <button className={styles.closeButton} onClick={toggleMenu}>
                    <X />
                </button>
                <nav>
                    <ul>
                        {[...menuItems, ...additionalMenuItems].map((item, index) => (
                            <li key={index}>
                                <Link to={item.path} ref={(el) => menuItemRefs.current[index] = el} onClick={toggleMenu}>
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <div className={styles.footer_social}>
                        <Link to="https://www.linkedin.com/company/magcrtagency/" target='_blank'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13.244" height="13.214" viewBox="0 0 13.244 13.214">
                                <g id="Group_9566" data-name="Group 9566" transform="translate(-8.69 -8.42)">
                                    <path id="Path_14545" data-name="Path 14545" d="M13.176,16.941h2.747v8.827H13.176Zm1.374-4.386a1.591,1.591,0,1,1-1.593,1.59,1.591,1.591,0,0,1,1.593-1.59" transform="translate(-4.267 -4.135)" fill="#fff" />
                                    <path id="Path_14546" data-name="Path 14546" d="M19.946,18.988h2.632v1.207h.036a2.885,2.885,0,0,1,2.6-1.426c2.778,0,3.29,1.827,3.29,4.2v4.842H25.76V23.523c0-1.025-.019-2.341-1.426-2.341-1.428,0-1.646,1.115-1.646,2.266v4.367H19.946Z" transform="translate(-6.568 -6.181)" fill="#fff" />
                                </g>
                            </svg>

                        </Link>
                        <Link to="https://www.instagram.com/magcrtagency?igsh=eWMxcXpla3R4OGI1" target='_blank'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13.97" height="13.97" viewBox="0 0 13.97 13.97">
                                <g id="Group_9567" data-name="Group 9567" transform="translate(-43.308 -9.141)">
                                    <path id="Path_14548" data-name="Path 14548" d="M80.1,17.348a.819.819,0,1,0,.819.819.819.819,0,0,0-.819-.819" transform="translate(-26.109 -5.713)" fill="#fff" />
                                    <path id="Path_14549" data-name="Path 14549" d="M73.385,18.915a3.44,3.44,0,1,0,3.441,3.44,3.445,3.445,0,0,0-3.441-3.44m0,5.644a2.2,2.2,0,1,1,2.2-2.2,2.206,2.206,0,0,1-2.2,2.2" transform="translate(-23.034 -6.229)" fill="#fff" />
                                    <path id="Path_14550" data-name="Path 14550" d="M74.347,27.6H68.77a4.2,4.2,0,0,1-4.2-4.2V17.825a4.2,4.2,0,0,1,4.2-4.2h5.577a4.2,4.2,0,0,1,4.2,4.2V23.4a4.2,4.2,0,0,1-4.2,4.2M68.77,14.944a2.885,2.885,0,0,0-2.882,2.882V23.4a2.885,2.885,0,0,0,2.882,2.882h5.577A2.885,2.885,0,0,0,77.229,23.4V17.825a2.885,2.885,0,0,0-2.882-2.882Z" transform="translate(-21.266 -4.488)" fill="#fff" />
                                </g>
                            </svg>


                        </Link>
                        <Link to="https://www.tiktok.com/@magcrtagency?_t=8rwCcbIULVg&_r=1" target='_blank'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="10.866" height="12.561" viewBox="0 0 10.866 12.561">
                                <g id="Group_9569" data-name="Group 9569" transform="translate(-76.184 -9.177)">
                                    <path id="Path_14555" data-name="Path 14555" d="M176.932,13.683h2.031a3.272,3.272,0,0,0,.667,1.909,3.523,3.523,0,0,0,2.439,1.221V18.9a5.5,5.5,0,0,1-3.019-.982v.309c-.005,1.372-.008,2.744-.017,4.116a3.913,3.913,0,0,1-7.286,1.967A4.713,4.713,0,0,1,171.2,22.7v-.834a.719.719,0,0,0,.043-.119,3.859,3.859,0,0,1,2.771-3.2,3.13,3.13,0,0,1,1.725-.1v2.245a5.485,5.485,0,0,0-.93.008,1.779,1.779,0,0,0-1.41,2.1,1.8,1.8,0,0,0,2.042,1.4,1.7,1.7,0,0,0,1.44-1.631c.019-.573.033-1.146.035-1.719q.012-3.582.015-7.165" transform="translate(-95.019 -4.506)" fill="#fff" />
                                </g>
                            </svg>

                        </Link>
                        <Link to="https://www.facebook.com/share/1XWd1Qwcyf/?mibextid=LQQJ4d" target='_blank'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="6.401" height="11.951" viewBox="0 0 6.401 11.951">
                                <g id="Group_9568" data-name="Group 9568" transform="translate(-108.426 -9.337)">
                                    <path id="Path_14552" data-name="Path 14552" d="M126.612,25.872V20.643h1.744l.331-2.163h-2.075v-1.4a1.081,1.081,0,0,1,1.219-1.169h.944V14.067a11.5,11.5,0,0,0-1.675-.146,2.64,2.64,0,0,0-2.826,2.911V18.48h-1.9v2.163h1.9v5.229Z" transform="translate(-13.948 -4.584)" fill="#fff" />
                                </g>
                            </svg>

                        </Link>
                    </div>
                </nav>
            </div>
            {/* Morphing Fullscreen Navigation */}
            <div
                className={`${styles.morphingNav} ${desktopDrawerOpen ? styles.open : ''}`}
                ref={morphingNavRef}
            >
                <button className={`${styles.closeButtonDsk} ${i18n.language === LANG_AR ? 'leftBtn' : ''} `} onClick={toggleDesktopDrawer}>
                    <X />
                </button>
                <nav className={`${styles.fullscreenNav} ${i18n.language === LANG_AR ? 'arab' : ''}`}>
                    <div className={styles.NavImg}>
                        {/* <img src={MLogo} alt="" /> */}
                    </div>
                    <ul className={styles.NavMenu}>
                        {[...menuItems, ...additionalMenuItems].map((item, index) => (
                            <li key={index}>
                                <Link to={item.path} ref={(el) => menuItemRefs.current[index] = el} onClick={toggleDesktopDrawer}>
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <div className={styles.footer_social}>
                        <Link to="https://www.linkedin.com/company/magcrtagency/" target='_blank'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13.244" height="13.214" viewBox="0 0 13.244 13.214">
                                <g id="Group_9566" data-name="Group 9566" transform="translate(-8.69 -8.42)">
                                    <path id="Path_14545" data-name="Path 14545" d="M13.176,16.941h2.747v8.827H13.176Zm1.374-4.386a1.591,1.591,0,1,1-1.593,1.59,1.591,1.591,0,0,1,1.593-1.59" transform="translate(-4.267 -4.135)" fill="#fff" />
                                    <path id="Path_14546" data-name="Path 14546" d="M19.946,18.988h2.632v1.207h.036a2.885,2.885,0,0,1,2.6-1.426c2.778,0,3.29,1.827,3.29,4.2v4.842H25.76V23.523c0-1.025-.019-2.341-1.426-2.341-1.428,0-1.646,1.115-1.646,2.266v4.367H19.946Z" transform="translate(-6.568 -6.181)" fill="#fff" />
                                </g>
                            </svg>

                        </Link>
                        <Link to="https://www.instagram.com/magcrtagency?igsh=eWMxcXpla3R4OGI1" target='_blank'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13.97" height="13.97" viewBox="0 0 13.97 13.97">
                                <g id="Group_9567" data-name="Group 9567" transform="translate(-43.308 -9.141)">
                                    <path id="Path_14548" data-name="Path 14548" d="M80.1,17.348a.819.819,0,1,0,.819.819.819.819,0,0,0-.819-.819" transform="translate(-26.109 -5.713)" fill="#fff" />
                                    <path id="Path_14549" data-name="Path 14549" d="M73.385,18.915a3.44,3.44,0,1,0,3.441,3.44,3.445,3.445,0,0,0-3.441-3.44m0,5.644a2.2,2.2,0,1,1,2.2-2.2,2.206,2.206,0,0,1-2.2,2.2" transform="translate(-23.034 -6.229)" fill="#fff" />
                                    <path id="Path_14550" data-name="Path 14550" d="M74.347,27.6H68.77a4.2,4.2,0,0,1-4.2-4.2V17.825a4.2,4.2,0,0,1,4.2-4.2h5.577a4.2,4.2,0,0,1,4.2,4.2V23.4a4.2,4.2,0,0,1-4.2,4.2M68.77,14.944a2.885,2.885,0,0,0-2.882,2.882V23.4a2.885,2.885,0,0,0,2.882,2.882h5.577A2.885,2.885,0,0,0,77.229,23.4V17.825a2.885,2.885,0,0,0-2.882-2.882Z" transform="translate(-21.266 -4.488)" fill="#fff" />
                                </g>
                            </svg>


                        </Link>
                        <Link to="https://www.tiktok.com/@magcrtagency?_t=8rwCcbIULVg&_r=1" target='_blank'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="10.866" height="12.561" viewBox="0 0 10.866 12.561">
                                <g id="Group_9569" data-name="Group 9569" transform="translate(-76.184 -9.177)">
                                    <path id="Path_14555" data-name="Path 14555" d="M176.932,13.683h2.031a3.272,3.272,0,0,0,.667,1.909,3.523,3.523,0,0,0,2.439,1.221V18.9a5.5,5.5,0,0,1-3.019-.982v.309c-.005,1.372-.008,2.744-.017,4.116a3.913,3.913,0,0,1-7.286,1.967A4.713,4.713,0,0,1,171.2,22.7v-.834a.719.719,0,0,0,.043-.119,3.859,3.859,0,0,1,2.771-3.2,3.13,3.13,0,0,1,1.725-.1v2.245a5.485,5.485,0,0,0-.93.008,1.779,1.779,0,0,0-1.41,2.1,1.8,1.8,0,0,0,2.042,1.4,1.7,1.7,0,0,0,1.44-1.631c.019-.573.033-1.146.035-1.719q.012-3.582.015-7.165" transform="translate(-95.019 -4.506)" fill="#fff" />
                                </g>
                            </svg>

                        </Link>
                        <Link to="https://www.facebook.com/share/1XWd1Qwcyf/?mibextid=LQQJ4d" target='_blank'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="6.401" height="11.951" viewBox="0 0 6.401 11.951">
                                <g id="Group_9568" data-name="Group 9568" transform="translate(-108.426 -9.337)">
                                    <path id="Path_14552" data-name="Path 14552" d="M126.612,25.872V20.643h1.744l.331-2.163h-2.075v-1.4a1.081,1.081,0,0,1,1.219-1.169h.944V14.067a11.5,11.5,0,0,0-1.675-.146,2.64,2.64,0,0,0-2.826,2.911V18.48h-1.9v2.163h1.9v5.229Z" transform="translate(-13.948 -4.584)" fill="#fff" />
                                </g>
                            </svg>

                        </Link>
                    </div>
                </nav>
            </div>
        </>
    );
});

Header.displayName = 'Header';
export default Header;
